import React from 'react';
import { motion } from 'framer-motion';
import { Server, Database, Globe } from 'lucide-react';
import { useThemeMode } from '../../hooks/useThemeMode';

const Architecture = () => {
  const { isDark } = useThemeMode();

  return (
    <section
      className={`py-24 border-t ${isDark ? 'border-neutral-900' : 'border-neutral-200'}`}
      style={{ backgroundColor: isDark ? '#0a0a0a' : '#f5f5f5' }}
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-sm font-mono text-neutral-500 mb-4 tracking-widest">TECHNICAL ARCHITECTURE</h2>
          <h3 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-neutral-900'}`}>Under the Hood</h3>
        </div>

        <div className="relative">
          {/* Animated Connecting Line */}
          <div className="hidden md:block absolute top-1/2 left-0 w-full z-0 -translate-y-1/2 overflow-visible">
            <svg className="w-full h-12 overflow-visible" preserveAspectRatio="none">
              <motion.line
                x1="0" y1="50%" x2="100%" y2="50%"
                stroke={isDark ? '#333' : '#d4d4d4'}
                strokeWidth="1"
                strokeDasharray="4 4"
              />
              <motion.line
                x1="0" y1="50%" x2="100%" y2="50%"
                stroke={isDark ? '#666' : '#737373'}
                strokeWidth="1"
                strokeDasharray="4 4"
                initial={{ pathLength: 0 }}
                whileInView={{ pathLength: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 1.5, ease: "easeInOut" }}
              />
            </svg>
          </div>

          <div className="grid md:grid-cols-3 gap-12">
            {[
              {
                icon: Globe,
                title: "Multi Ecosystem",
                desc: "Solana SPL tokens + ERC-20 tokens with unified treasury system."
              },
              {
                icon: Database,
                title: "Backend Node",
                desc: "Validates deposits on EVM and Solana chains, manages key distribution."
              },
              {
                icon: Server,
                title: "Dual Keys",
                desc: "Ed25519 for Solana, ECDSA for EVM - automatic key type selection."
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                whileHover={{ scale: 1.05 }}
                className={`relative border p-8 text-center z-10 transition-colors ${
                  isDark ? 'border-neutral-800 hover:border-neutral-600' : 'border-neutral-200 hover:border-neutral-400'
                }`}
                style={{ backgroundColor: isDark ? '#050505' : '#ffffff' }}
              >
                <div
                  className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6 border ${isDark ? 'border-neutral-800' : 'border-neutral-200'}`}
                  style={{
                    backgroundColor: isDark ? '#171717' : '#f5f5f5',
                    boxShadow: isDark ? '0 0 15px rgba(255,255,255,0.05)' : '0 0 15px rgba(0,0,0,0.05)',
                  }}
                >
                  <item.icon size={24} className={isDark ? 'text-white' : 'text-neutral-900'} />
                </div>
                <h4 className={`text-xl font-bold mb-3 ${isDark ? 'text-white' : 'text-neutral-900'}`}>{item.title}</h4>
                <p className={`text-sm ${isDark ? 'text-neutral-400' : 'text-neutral-500'}`}>{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Architecture;
